import {gql} from '@apollo/client';
import {noop, objectFromEntries} from '@benchling/js-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import PropTypes from 'prop-types';
import React, {useEffect} from 'react';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import ProteinAlignmentModal from 'analyses/aa-sequence-alignment/AaSequenceAlignmentModal';
import CreateAnalysisModal from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import isCreateAnalysisEnabled from 'analysis-tool/isCreateAnalysisEnabled';
import * as analytics from 'analytics/Analytics';
import {trackEvent} from 'analytics/Analytics';
import AntibodyCreateModal from 'antibodies/actions/AntibodyCreateModal';
import NestedCreateAndRegisterAntibodiesModal from 'antibodies/nested-data-creation/NestedCreateAndRegisterAntibodiesModal';
import NestedCreateAndRegisterAntibodiesSpreadsheetImportModal from 'antibodies/nested-data-creation/spreadsheet-import/NestedCreateAndRegisterAntibodiesSpreadsheetImportModal';
import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import isArchived from 'archive/isArchived';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {
  InsightsFeature,
  InventoryFeature,
  LabAutomationFeature,
  MolbioFeature,
  ProcessesFeature,
  RegistryFeature,
  V2RequestsFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {
  areAnyFeaturesEnabled,
  isConnectFeatureEnabled,
  isFeatureEnabled,
} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import BulkCreateBoxesFlow from 'bulk-import/BulkCreateBoxesFlow';
import BulkCreateContainersFlow from 'bulk-import/BulkCreateContainersFlow';
import BulkCreateLocationsFlow from 'bulk-import/BulkCreateLocationsFlow';
import BulkCreatePlatesFlow from 'bulk-import/BulkCreatePlatesFlow';
import BulkUpdateBoxesFlow from 'bulk-import/BulkUpdateBoxesFlow';
import BulkUpdateContainersFlow from 'bulk-import/BulkUpdateContainersFlow';
import {openBulkUpdateLocationsFlow} from 'bulk-import/BulkUpdateLocationsFlow';
import BulkUpdatePlatesFlow from 'bulk-import/BulkUpdatePlatesFlow';
import {TagSchemasSSIQuery} from 'bulk-import/panes/__generated/TagSchemasSSIQuery';
import {TAG_SCHEMAS_SSI_QUERY} from 'bulk-import/panes/EntitySchemaImplementingSSISelectionWithRestrictions';
import {ActionOrigin, ANALYTICS_MODULE, LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {NavStoreRegistry, OligoAwareFolderItemType} from 'constants/RegistryConstants';
import {getOwnerChoices} from 'create-components/OwnableUtil';
import CreateBioentityModal from 'folder-items/CreateBioentityModal';
import {CREATE_MODAL_DATA, CreateModal} from 'folder-items/CreateModalsConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useBenchlingQuery, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import CreateAnalyticsDashboardModal from 'insights/sidepanel/CreateAnalyticsDashboardModal';
import CreateConnectionModal from 'instruments/connections/CreateConnectionModal';
import {openDraftChatInSidepanel} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import {isAgentTypeEnabled} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {
  CreateMenuComplexPolymersQuery,
  CreateMenuComplexPolymersQueryVariables,
} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  CREATE_MENU_COMPLEX_POLYMERS_QUERY,
  getComplexPolymerSchemaConfigs,
  isComplexPolymerSchema,
  useComplexPolymerSchemasAsLegacyCreateDropdownOptions,
} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import {ComplexPolymerSchemaConfigGraphQL} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {openImportProteinModal} from 'molbio/aa-sequences/ImportAaSequenceModal';
import {BulkAssemblyMethod, FragmentProductionMethod} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  handleNewCloningBulkAssemblyClick,
  handleNewConcatenationBulkAssemblyClick,
} from 'molbio/bulk-assembly/util/BulkAssemblyUtil';
import {
  BIOENTITY_CREATION_LOCATIONS,
  DNA_SEQUENCE_IMPORT_FLOW,
  OLIGO_CREATION_METHODS,
  AA_SEQUENCE_IMPORT_MODAL_TAB,
} from 'molbio/MolBioConstants';
import {trackClickOnCreateNewOligo} from 'molbio/SequenceAnalyticsHelper';
import {
  openBulkAssembleDNA,
  openBulkUpdateBioentitiesModal,
  openCreateMoleculeModal,
  openImportMoleculesFromSpreadsheet,
  openImportProteinsFromSpreadsheet,
  openImportSequencesFromSpreadsheet,
} from 'nav/CreateDropdownActions';
import {AllSourcesQuery} from 'nav/graphql/__generated/AllSourcesQuery';
import {ALL_SOURCES_QUERY} from 'nav/graphql/AllSources';
import {AllConnectionSchemasQuery} from 'nav/navbar/__generated/AllConnectionSchemasQuery';
import {AllAssaySchemasQuery} from 'nav/sidepanel/__generated/AllAssaySchemasQuery';
import {ALL_ASSAY_SCHEMAS_QUERY} from 'nav/sidepanel/SidepanelQueries';
import CreateEntryFromTemplateModal from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import LegacyCreateEntryFromTemplateModalClass from 'notebook/entry-templates/LegacyCreateEntryFromTemplateModal';
import {
  getProcessTaskSchemaGlobalCreateOptions,
  ProcessTaskSchemaForCreate,
  useProcessTaskSchemasForCreate,
} from 'processes/CreateProcessUtil';
import CreateRequestV2SubmissionModal from 'processes/requests_v2/modals/CreateRequestV2SubmissionModal';
import {getStore} from 'redux-stores/Store';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import {getInitialRegistry} from 'registry/DefaultRegistry';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {TRIAL_SAMPLE_SCHEMA_INTERFACE_ID} from 'registry/trial-interfaces/TrialInterfaceConstants';
import openCreateRequestModal from 'requests/dashboard/openCreateRequestModal';
import useAllRequestTaskSchemasQuery from 'requests/graphql/useAllRequestTaskSchemasQuery';
import useAllRequestTemplatesQuery from 'requests/graphql/useAllRequestTemplatesQuery';
import {getLegacyRequestObjectName, RequestSchema} from 'requests/RequestConstants';
import getInitialRequestProps from 'requests/templates/getInitialRequestProps';
import * as CreateContainerModal from 'samples/containers/CreateContainerModal';
import CreateBoxModal from 'samples/CreateBoxModal';
import CreateLocationModal from 'samples/CreateLocationModal';
import CreatePlateModal from 'samples/CreatePlateModal';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import UpgradeAccountModal, {ENTERPRISE_FEATURES_KEYS} from 'start/upgrade-account/UpgradeAccountModal';
import {useStudySchemasAsLegacyDropdownMenuItems} from 'studies/sidepanel/CreateStudyMenu';
import {StudyAssociation} from 'studies/StudyableDAO';
import {filterTagSchemasGraphQL} from 'tag-schema/filterTagSchemas';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/legacy/LegacyDropdownMenuConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';
import {ElementMouseEvent} from 'util/Types';
import CreateWorkflowModal from 'workflows/modals/CreateWorkflowModal';
import {getWorkflowDisplayString} from 'workflows/util/WorkflowUtil';

import {RequestTemplateGraphQL} from '../../../graphql-fragments/requests/__generated/RequestTemplateGraphQL';
import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';

type ConnectionSchema = AllConnectionSchemasQuery['connectionSchemas'][number];

type CreateMenuItem = LegacyDropdownMenuItem & {
  value: string;
  submenu?: Array<Array<CreateMenuItem>>;
  pureTextName?: string;
};

const ACTION_ORIGIN = ActionOrigin.SIDEPANEL_GLOBAL_CREATE;
const LOADING_MENU_ITEM: LegacyDropdownMenuItem = {name: 'Loading...', disabled: true};

function EntryFromComposeLabel(): JSX.Element {
  useEffect(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.VIEWED_GLOBAL_ENTRY_FROM_COMPOSE, {
      actionOrigin: ActionOrigin.SIDEPANEL_GLOBAL_CREATE,
    });
  }, []);
  return <>Entry from Compose</>;
}

function gateFeature(menuItem: CreateMenuItem, featureKey: string): CreateMenuItem {
  if (!menuItem.visible && UpgradeAccountModal.isEnabled()) {
    return {
      name: menuItem.name,
      icon: 'star',
      handleOnClick: () => {
        UpgradeAccountModal.open(featureKey);
      },
      visible: true,
      extraIconClasses: 'navDropdownMenu-item-icon--green',
      value: `upgrade-${menuItem.value}`,
    };
  }
  return menuItem;
}

interface Props {
  children: JSX.Element;
  connectionSchemas: Array<ConnectionSchema> | null;
  registries: Array<NavStoreRegistry>;
  tagSchemas: Array<TagSchemaListingWithFieldsGraphQL> | null;
  interfaceIdsByTagSchemaIds: Record<string, Array<string>> | null;
  requestSchemas: Array<RequestSchema> | null;
  processTaskSchemas: Array<ProcessTaskSchemaForCreate>;
  requestTemplatesById: Record<string, RequestTemplateGraphQL>;
  requestTaskSchemasById: Record<string, RequestTaskSchemaGraphQL>;
  actionOrigin: ActionOrigin;
  studySchemaCreateOptions: Array<CreateMenuItem>;
  complexPolymerSchemaCreateOptions: Array<CreateMenuItem>;
  complexPolymerSchemaConfigurations: Array<ComplexPolymerSchemaConfigGraphQL>;
  isOpen: boolean;
  onToggle: (newIsOpen: boolean) => void;
}

class PresentationalCreateMenu extends React.Component<Props> {
  static propTypes = {
    registries: PropTypes.arrayOf(RegistryConstants.REGISTRY_SHAPE.isRequired).isRequired,
  };

  _handleCreateEntryFromTemplate = ({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    studyAssociations,
  }: {
    entryName: string;
    folderId: string;
    templateId: string;
    tagSchemaId?: string;
    schemaTags?: TagUpdateObject;
    initialDay?: string;
    studyAssociations?: Array<StudyAssociation>;
  }): void => {
    EntryActions.createFromTemplate({
      entryName,
      folderId,
      templateId,
      tagSchemaId,
      schemaTags,
      initialDay,
      studyAssociations,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      onSuccess: LegacyCreateEntryFromTemplateModalClass.close,
    });
  };

  trackCreateSequence(importFlow: string): void {
    analytics.track('sequence', 'open_create_sequence_modal', {
      product: ANALYTICS_MODULE.MOLBIO,
      createdFrom: BIOENTITY_CREATION_LOCATIONS.GLOBAL,
      initialImportFlow: importFlow,
    });
  }

  _getMenuSections(): Array<Array<CreateMenuItem>> {
    return [
      [
        {
          name: 'Project',
          icon: ModelTypeConstants.SOURCE,
          handleOnClick: CREATE_MODAL_DATA[CreateModal.PROJECT].callback,
          visible: getOwnerChoices().length > 0,
          value: 'create-project',
        },
      ],
      [
        this.getEntryMenuItem(),
        {
          name: 'Protocol',
          icon: ModelTypeConstants.PROTOCOL,
          handleOnClick: CREATE_MODAL_DATA[CreateModal.PROTOCOL].callback,
          visible: BenchlingConfig.ENABLE_PROTOCOLS,
          value: 'create-protocol',
        },
      ],
      [
        {
          name: 'DNA / RNA sequence',
          icon: 'helix',
          value: 'create-nucleotide-sequences',
          submenu: [
            [
              {
                name: 'New DNA / RNA sequence',
                icon: FolderItemIcon.Sequence,
                handleOnClick: () => {
                  CREATE_MODAL_DATA[CreateModal.SEQUENCE].callback();
                  this.trackCreateSequence(DNA_SEQUENCE_IMPORT_FLOW.RAW);
                },
                value: 'create-new-nucleotide-sequence',
                visible: isFeatureEnabled({
                  feature: StandardFeature.CREATE,
                  itemType: ModelTypeConstants.SEQUENCE,
                }),
              },
              {
                name: 'New DNA / RNA alignment',
                icon: 'alignment',
                handleOnClick: CREATE_MODAL_DATA[CreateModal.ALIGNMENT].callback,
                visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                value: 'create-new-nucleotide-alignment',
              },
              {
                name: 'Import DNA / RNA sequences',
                icon: 'import',
                visible: isFeatureEnabled({
                  feature: StandardFeature.IMPORT,
                  itemType: ModelTypeConstants.SEQUENCE,
                }),
                handleOnClick: () => {
                  this.trackCreateSequence(DNA_SEQUENCE_IMPORT_FLOW.FILE);
                  CREATE_MODAL_DATA[CreateModal.SEQUENCE_IMPORT].callback();
                },
                value: 'import-nucleotide-sequence',
              },
              {
                name: 'Import DNA / RNA sequences from spreadsheet',
                icon: 'import',
                handleOnClick: () => {
                  openImportSequencesFromSpreadsheet(null);
                },
                visible: isFeatureEnabled({
                  feature: StandardFeature.IMPORT,
                  itemType: ModelTypeConstants.SEQUENCE,
                }),
                value: 'import-nucleotide-sequence-from-spreadsheet',
              },
              {
                name: 'Bulk import DNA / RNA from genome',
                icon: 'import',
                handleOnClick: CREATE_MODAL_DATA[CreateModal.BATCH_SEQUENCE_IMPORT].callback,
                visible:
                  BenchlingConfig.ENABLE_BATCH_SEQUENCE_IMPORT &&
                  isFeatureEnabled({feature: StandardFeature.IMPORT, itemType: ModelTypeConstants.SEQUENCE}),
                value: 'bulk-import-nucleotide-sequence-from-genome',
              },
              gateFeature(
                {
                  name: 'Assemble DNA sequences by cloning',
                  icon: ModelTypeConstants.BULK_ASSEMBLY,
                  handleOnClick: openBulkAssembleDNA,
                  visible:
                    BenchlingConfig.ENABLE_BULK_ASSEMBLY &&
                    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                  value: 'assemble-nucleotide-sequences-by-cloning',
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
              gateFeature(
                {
                  name: 'Assemble DNA / RNA sequences by concatenation',
                  icon: ModelTypeConstants.BULK_ASSEMBLY,
                  handleOnClick: () => {
                    handleNewConcatenationBulkAssemblyClick(
                      {
                        initialMethodData: {
                          type: BulkAssemblyMethod.CONCATENATION,
                          parameters: {
                            polymerSubtype: PolymerSubtype.dna_sequence,
                            defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                            applyDefaultNullTerminalPhosphate: true,
                          },
                        },
                      },
                      ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
                    );
                  },
                  visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                  value: 'assemble-nucleotide-sequences-by-concatenation',
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
            ],
          ],
        },
        {
          name: 'AA sequence',
          icon: 'protein',
          visible: true,
          value: 'protein',
          submenu: [
            [
              {
                name: 'New AA sequence',
                icon: 'protein',
                handleOnClick: () => {
                  openImportProteinModal({
                    initialTab: AA_SEQUENCE_IMPORT_MODAL_TAB.CREATE,
                    initialFolderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
                  });
                },
                visible: isFeatureEnabled({
                  feature: StandardFeature.CREATE,
                  itemType: ModelTypeConstants.PROTEIN,
                }),
                value: 'create-aa-sequence',
              },
              {
                name: 'New AA alignment',
                icon: 'alignment',
                visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                handleOnClick: () => {
                  ProteinAlignmentModal.open({initialProteinSpecifiers: []});
                },
                value: 'create-aa-alignment',
              },
              {
                name: 'Import AA sequences',
                icon: 'import',
                handleOnClick: () => {
                  analytics.track('protein', 'open_import_aa_modal', {
                    product: ANALYTICS_MODULE.MOLBIO,
                    createdFrom: BIOENTITY_CREATION_LOCATIONS.GLOBAL,
                  });
                  openImportProteinModal({initialTab: AA_SEQUENCE_IMPORT_MODAL_TAB.FILE});
                },
                visible: isFeatureEnabled({
                  feature: StandardFeature.IMPORT,
                  itemType: ModelTypeConstants.PROTEIN,
                }),
                value: 'import-aa-sequences',
              },
              {
                name: 'Import AA sequences from spreadsheet',
                icon: 'import',
                handleOnClick: () => {
                  openImportProteinsFromSpreadsheet(null);
                },
                visible: isFeatureEnabled({
                  feature: StandardFeature.IMPORT,
                  itemType: ModelTypeConstants.PROTEIN,
                }),
                value: 'import-proteins-from-spreadsheet',
              },
              {
                name: 'Assemble AA sequences by concatenation',
                icon: ModelTypeConstants.BULK_ASSEMBLY,
                handleOnClick: () => {
                  handleNewConcatenationBulkAssemblyClick(
                    {
                      initialMethodData: {
                        type: BulkAssemblyMethod.CONCATENATION,
                        parameters: {
                          polymerSubtype: PolymerSubtype.aa_sequence,
                          defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                          applyDefaultNullTerminalPhosphate: true,
                        },
                      },
                    },
                    ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
                  );
                },
                visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                value: 'assemble-proteins-by-concatenation',
              },
              gateFeature(
                {
                  name: 'Bulk translate DNA to AA',
                  icon: ModelTypeConstants.PROTEIN,
                  handleOnClick: () => {
                    benchlingUtil.navigate(null, '/bulk-workflows/translation');
                  },
                  link: '/bulk-workflows/translation',
                  value: 'bulk-translate-dna-to-aa',
                  visible:
                    BenchlingConfig.ENABLE_BULK_ANTIBODY &&
                    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
            ],
          ],
        },
        {
          name: 'Oligo',
          icon: FolderItemIcon.OligoV2,
          value: 'oligo',
          submenu: [
            [
              {
                name: 'New DNA / RNA oligo',
                icon: FolderItemIcon.OligoV2,
                handleOnClick: () => {
                  CREATE_MODAL_DATA[CreateModal.OLIGO].callback();
                  trackClickOnCreateNewOligo(
                    BIOENTITY_CREATION_LOCATIONS.GLOBAL_CREATE_BUTTON,
                    OLIGO_CREATION_METHODS.MODAL
                  );
                },
                visible: isFeatureEnabled({
                  feature: StandardFeature.CREATE,
                  itemType: ModelTypeConstants.SEQUENCE,
                }),
                value: 'create-new-oligo',
              },
              {
                name: 'Import DNA / RNA oligos from spreadsheet',
                icon: 'import',
                visible: isFeatureEnabled({
                  feature: StandardFeature.IMPORT,
                  itemType: ModelTypeConstants.SEQUENCE,
                }),
                handleOnClick: () => {
                  trackClickOnCreateNewOligo(
                    BIOENTITY_CREATION_LOCATIONS.GLOBAL_CREATE_BUTTON,
                    OLIGO_CREATION_METHODS.SPREADSHEET
                  );
                  CREATE_MODAL_DATA[CreateModal.OLIGO_IMPORT].callback();
                },
                value: 'import-oligos-from-spreadsheet',
              },
              gateFeature(
                {
                  name: 'Assemble DNA / RNA oligos by concatenation',
                  icon: ModelTypeConstants.BULK_ASSEMBLY,
                  handleOnClick: () => {
                    handleNewConcatenationBulkAssemblyClick(
                      {
                        initialMethodData: {
                          type: BulkAssemblyMethod.CONCATENATION,
                          parameters: {
                            polymerSubtype: PolymerSubtype.dna_oligo,
                            defaultFragmentProductionMethod: FragmentProductionMethod.SLICE,
                            applyDefaultNullTerminalPhosphate: true,
                          },
                        },
                      },
                      ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
                    );
                  },
                  visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                  value: 'assemble-oligos-by-concatenation',
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
            ],
          ],
        },
        {
          name: 'Oligo duplex or oligo conjugate',
          icon: 'network-wired',
          visible: BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
          value: 'complex-polymer',
          submenu: [this.props.complexPolymerSchemaCreateOptions],
        },
        {
          name: 'Protein',
          icon: 'antibody',
          visible: BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_ENTITIES,
          value: 'protein',
          submenu: [
            [
              {
                name: 'Create proteins',
                icon: 'import',
                handleOnClick: () => {
                  if (BenchlingConfig.ANTIBODY_NESTED_DATA_CREATION_USE_SPREADSHEET_IMPORT) {
                    runModal(NestedCreateAndRegisterAntibodiesSpreadsheetImportModal, {
                      analyticsMetadata: {
                        actionOrigin: ACTION_ORIGIN,
                        eventGroupId: analytics.makeAnalyticsEventId(),
                      },
                    });
                  } else {
                    runModal(NestedCreateAndRegisterAntibodiesModal, {
                      analyticsMetadata: {
                        actionOrigin: ACTION_ORIGIN,
                        eventGroupId: analytics.makeAnalyticsEventId(),
                      },
                    });
                  }
                },
                visible: BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_NESTED_DATA_CREATION,
                value: 'create-antibodies-from-pairs',
              },
              {
                name: 'Create single protein',
                icon: 'antibody',
                handleOnClick: () => {
                  runModal(AntibodyCreateModal, {initialFolderId: null});
                },
                visible: BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_SINGLE_CREATE,
                value: 'create-single-antibody',
              },
            ],
          ],
        },
        {
          name: 'Assembly',
          icon: ModelTypeConstants.BULK_ASSEMBLY,
          value: 'assembly',
          submenu: [
            [
              gateFeature(
                {
                  name: 'Assemble DNA sequences by cloning',
                  icon: ModelTypeConstants.BULK_ASSEMBLY,
                  visible:
                    BenchlingConfig.MOLBIO_REDESIGNED_CLONING_FLOW &&
                    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                  handleOnClick: () => {
                    handleNewCloningBulkAssemblyClick({}, ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL);
                  },
                  value: 'assembly-dna-sequences-by-cloning',
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
              gateFeature(
                {
                  name: 'Assemble sequences and oligos by concatenation',
                  icon: ModelTypeConstants.BULK_ASSEMBLY,
                  visible: isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                  handleOnClick: () => {
                    handleNewConcatenationBulkAssemblyClick(
                      {},
                      ActionOrigin.MOLBIO_BULK_ASSEMBLY_SETTINGS_MODAL
                    );
                  },
                  value: 'assemble-sequences-and-oligos-by-concatenation',
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
            ],
          ],
        },
        {
          name: 'CRISPR',
          icon: 'crispr',
          value: 'crispr',
          submenu: [
            [
              {
                name: 'CRISPR guides',
                icon: 'crispr',
                handleOnClick: CREATE_MODAL_DATA[CreateModal.CRISPR].callback,
                visible:
                  BenchlingConfig.ENABLE_CRISPR && isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                value: 'create-crispr-guides',
              },
              {
                name: 'HR template',
                icon: 'hr-template',
                handleOnClick: CREATE_MODAL_DATA[CreateModal.HR_TEMPLATE].callback,
                visible:
                  BenchlingConfig.ENABLE_CRISPR && isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                value: 'create-hr-template',
              },
            ],
          ],
        },
        {
          name: 'Entity from schema',
          dropup: true,
          icon: FolderItemIcon.BasicFolderItem,
          visible: areAnyFeaturesEnabled([
            {feature: RegistryFeature.REGISTRY_CREATE},
            {feature: RegistryFeature.REGISTRY_IMPORT},
          ]),
          value: 'entity-from-schema',
          submenu: [
            this.getTagSchemaMenuItems({
              folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
              schemaFilterFn: (tagSchema: TagSchemaListingWithFieldsGraphQL) =>
                !isComplexPolymerSchema(this.props.complexPolymerSchemaConfigurations, tagSchema.id) &&
                !(this.props.interfaceIdsByTagSchemaIds?.[tagSchema.id] ?? []).includes(
                  TRIAL_SAMPLE_SCHEMA_INTERFACE_ID
                ) &&
                !(this.props.interfaceIdsByTagSchemaIds?.[tagSchema.id] ?? []).includes(
                  ANTIBODY_SCHEMA_INTERFACE_ID
                ),
              tagSchemaSubtype: 'all',
            }),
            [
              {
                name: 'Import',
                icon: 'table',
                handleOnClick: CREATE_MODAL_DATA[CreateModal.ENTITIES_FROM_SCHEMAS].callback,
                visible: isFeatureEnabled({feature: RegistryFeature.REGISTRY_IMPORT}),
                value: 'import-entities-from-schemas',
              },
              // TODO(BNCH-131842): Remove the bulk create antibodies option, in favor of the NDC
              // "Create antibodies" path
              gateFeature(
                {
                  name: 'Bulk create antibodies',
                  icon: 'antibody',
                  handleOnClick: () => {
                    benchlingUtil.navigate(null, '/bulk-workflows/antibody');
                  },
                  link: '/bulk-workflows/antibody',
                  visible:
                    BenchlingConfig.ENABLE_BULK_ANTIBODY &&
                    isFeatureEnabled({feature: MolbioFeature.MOLBIO_CREATE}),
                  value: 'bulk-create-antibodies',
                },
                ENTERPRISE_FEATURES_KEYS.BULK_MOLECULAR_BIOLOGY
              ),
            ],
          ],
        },
        {
          name: 'Mixture',
          dropup: true,
          icon: FolderItemIcon.Mixture,
          visible:
            isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES}) &&
            areAnyFeaturesEnabled([
              {feature: RegistryFeature.REGISTRY_CREATE},
              {feature: RegistryFeature.REGISTRY_IMPORT},
            ]),
          value: 'mixture',
          submenu: [
            this.getTagSchemaMenuItems({
              folderItemType: ModelTypeConstants.MIXTURE,
              tagSchemaSubtype: 'mixture',
            }),
            [
              {
                name: 'Import',
                icon: 'table',
                handleOnClick: CREATE_MODAL_DATA[CreateModal.ENTITIES_FROM_SCHEMAS].callback,
                visible: isFeatureEnabled({feature: RegistryFeature.REGISTRY_IMPORT}),
                value: 'import-mixtures',
              },
            ],
          ],
        },
        {
          name: 'Molecule',
          icon: FolderItemIcon.Molecule,
          visible: Boolean(BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE),
          value: 'molecule',
          submenu: [
            [
              {
                name: 'New molecule',
                icon: FolderItemIcon.Molecule,
                handleOnClick: () => {
                  openCreateMoleculeModal(null);
                },
                value: 'new-molecule',
              },
              {
                name: 'Import molecules',
                icon: 'import',
                handleOnClick: () => {
                  openImportMoleculesFromSpreadsheet(null);
                },
                value: 'import-molecules',
              },
            ],
          ],
        },
      ],
      [
        {
          name: 'Inventory',
          dropup: true,
          icon: ModelTypeConstants.BOX,
          visible: areAnyFeaturesEnabled([
            {feature: InventoryFeature.INVENTORY_CREATE},
            {feature: InventoryFeature.INVENTORY_EDIT},
            {feature: InventoryFeature.INVENTORY_IMPORT},
          ]),
          value: 'inventory',
          submenu: [
            [
              {
                name: 'New location',
                icon: ModelTypeConstants.LOCATION,
                handleOnClick: () => {
                  CreateLocationModal.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    initialParentStorable: null,
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible:
                  isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE}) &&
                  this.props.registries &&
                  this.props.registries.some((registry) => registry.permissions.locationsCreatable),
                value: 'new-location',
              },
              {
                name: 'Import locations',
                icon: 'import',
                handleOnClick: () => {
                  BulkCreateLocationsFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible:
                  this.props.registries &&
                  this.props.registries.some((registry) => registry.permissions.locationsCreatable),
                value: 'import-locations',
              },
              {
                name: 'Update locations',
                icon: 'table',
                handleOnClick: () => {
                  openBulkUpdateLocationsFlow({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT}),
                value: 'update-locations',
              },
            ],
            [
              {
                name: 'New box',
                icon: ModelTypeConstants.BOX,
                handleOnClick: () => {
                  CreateBoxModal.open({
                    onRequestClose: noop,
                    initialRegistry: getInitialRegistry(this.props.registries),
                    initialParentStorable: null,
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE}),
                value: 'create-new-box',
              },
              {
                name: 'Import boxes',
                icon: 'import',
                handleOnClick: () => {
                  BulkCreateBoxesFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_IMPORT}),
                value: 'import-boxes',
              },
              {
                name: 'Update boxes',
                icon: 'table',
                handleOnClick: () => {
                  BulkUpdateBoxesFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT}),
                value: 'update-boxes',
              },
            ],
            [
              {
                name: 'New plate',
                icon: ModelTypeConstants.PLATE,
                handleOnClick: () => {
                  CreatePlateModal.open({
                    initialParentStorable: null,
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE}),
                value: 'create-new-plate',
              },
              {
                name: 'Import plates',
                icon: 'import',
                handleOnClick: () => {
                  BulkCreatePlatesFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_IMPORT}),
                value: 'import-plates',
              },
              {
                name: 'Update plates',
                icon: 'table',
                handleOnClick: () => {
                  BulkUpdatePlatesFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT}),
                value: 'update-plates',
              },
            ],
            [
              {
                name: 'New container',
                icon: ModelTypeConstants.CONTAINER,
                handleOnClick: () => {
                  CreateContainerModal.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    initialParentStorableId: null,
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_CREATE}),
                value: 'create-new-container',
              },
              {
                name: 'Import containers',
                icon: 'import',
                handleOnClick: () => {
                  BulkCreateContainersFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_IMPORT}),
                value: 'import-containers',
              },
              {
                name: 'Update containers',
                icon: 'table',
                handleOnClick: () => {
                  BulkUpdateContainersFlow.open({
                    initialRegistry: getInitialRegistry(this.props.registries),
                    actionOrigin: ACTION_ORIGIN,
                  });
                },
                visible: isFeatureEnabled({feature: InventoryFeature.INVENTORY_EDIT}),
                value: 'update-containers',
              },
            ],
          ],
        },
      ],
      [
        {
          name: 'Study',
          dropup: true,
          icon: ModelTypeConstants.STUDY,
          visible: BenchlingConfig.STUDIES_ENABLE,
          submenu: [this.props.studySchemaCreateOptions],
          value: 'create-study',
        },
      ],
      [
        {
          name: getWorkflowDisplayString('Legacy workflow', 'Workflow'),
          dropup: true,
          icon: ModelTypeConstants.WORKFLOW,
          visible: BenchlingConfig.LEGACY_WORKFLOWS_ENABLE,
          handleOnClick: () => {
            runModal(CreateWorkflowModal, {});
          },
          value: 'create-workflow',
        },
      ],
      [
        {
          name: 'Workflow tasks',
          dropup: true,
          icon: ModelTypeConstants.PROCESS,
          visible: isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE}),
          submenu: [getProcessTaskSchemaGlobalCreateOptions(this.props.processTaskSchemas)],
          dataTestAttrs: {component: 'CreateMenu', element: 'workflowTasks'},
          value: 'workflow-tasks',
        },
      ],
      [
        {
          name: getLegacyRequestObjectName(),
          dropup: true,
          icon: ModelTypeConstants.REQUEST,
          visible: BenchlingConfig.ENABLE_REQUESTS,
          submenu: [this.getRequestSchemaMenuItems()],
          dataTestAttrs: {component: 'CreateMenu', element: 'request'},
          value: 'create-legacy-request',
        },
      ],
      [
        {
          name: 'Request',
          dropup: true,
          icon: ModelTypeConstants.REQUEST_V2_SUBMISSION,
          visible: isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_CREATE}),
          handleOnClick: () => {
            runModal(CreateRequestV2SubmissionModal, {
              actionOrigin: ACTION_ORIGIN,
            });
          },
          dataTestAttrs: {component: 'CreateMenu', element: 'requestV2Submission'},
          value: 'create-request',
        },
      ],
      this.getInsightsMenuItem(),
      [
        {
          name: 'Connection',
          dropup: true,
          icon: ModelTypeConstants.CONNECTION,
          visible: isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_CREATE}),
          submenu: [this.getConnectionSchemaMenuItems()],
          value: 'create-connection',
        },
      ],
      [
        {
          name: 'More',
          dropup: true,
          value: 'more',
          submenu: [
            [
              {
                name: 'File',
                icon: ModelTypeConstants.FILE,
                handleOnClick: CREATE_MODAL_DATA[CreateModal.FILE].callback,
                value: 'create-file',
              },
              {
                name: 'Folder',
                icon: ModelTypeConstants.FOLDER,
                handleOnClick: CREATE_MODAL_DATA[CreateModal.FOLDER].callback,
                value: 'create-folder',
              },
              {
                name: 'Template',
                icon: ModelTypeConstants.TEMPLATE_COLLECTION,
                handleOnClick: CREATE_MODAL_DATA[CreateModal.TEMPLATE].callback,
                value: 'create-template',
              },
              {
                name: 'Update entities',
                icon: 'table',
                visible: isFeatureEnabled({feature: RegistryFeature.REGISTRY_EDIT}),
                handleOnClick: () => {
                  openBulkUpdateBioentitiesModal(null, this.props.complexPolymerSchemaConfigurations);
                },
                value: 'update-entities',
              },
            ],
          ],
          dataTestAttrs: {component: 'CreateMenu', element: 'more'},
        },
      ],
    ];
  }

  private getInsightsMenuItem(): Array<CreateMenuItem> {
    if (isCreateAnalysisEnabled() && isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE})) {
      return [
        {
          name: 'Insights',
          icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
          visible: isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE}),
          value: 'insights',
          submenu: [
            [
              this.getCreateDashboardItem(),
              {
                name: 'Analysis',
                icon: ModelTypeConstants.ANALYSIS,
                visible: isCreateAnalysisEnabled(),
                handleOnClick: () => {
                  runModal(CreateAnalysisModal, {});
                },
                value: 'create-analysis',
              },
            ],
          ],
        },
      ];
    }
    return [
      this.getCreateDashboardItem(),
      {
        name: 'Analysis',
        icon: ModelTypeConstants.ANALYSIS,
        visible: isCreateAnalysisEnabled(),
        handleOnClick: () => {
          runModal(CreateAnalysisModal, {});
        },
        value: 'create-analysis',
      },
    ];
  }

  private getCreateDashboardItem(): CreateMenuItem {
    return {
      name: 'Dashboard',
      icon: ModelTypeConstants.ANALYTICS_DASHBOARD,
      visible: isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE}),
      handleOnClick: () => {
        runModal(CreateAnalyticsDashboardModal, {});
      },
      value: 'create-dashboard',
    };
  }

  private getEntryMenuItem(): CreateMenuItem {
    const newEntryFromTemplate = (): void => {
      // TODO(BNCH-38468): Remove feature flag after rollout and discontinue legacy modal
      if (!BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL) {
        LegacyCreateEntryFromTemplateModalClass.open({
          initialFolderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK),
          onRequestSubmit: this._handleCreateEntryFromTemplate,
        });
      } else {
        runModal(CreateEntryFromTemplateModal, {
          initialFolderId: FolderDefaults.get(FolderDefaultsKey.NOTEBOOK),
          onRequestClose: () => {},
          onRequestSubmit: this._handleCreateEntryFromTemplate,
          isTemplateSelectorDisabled: false,
        });
      }
    };

    const entrySubmenuItems: Array<CreateMenuItem> = [];

    if (BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION) {
      entrySubmenuItems.push({
        name: 'Blank entry',
        icon: FolderItemIcon.Entry,
        handleOnClick: CREATE_MODAL_DATA[CreateModal.ENTRY].callback,
        value: 'create-blank-entry',
      });
    }

    entrySubmenuItems.push({
      name: 'Entry from template',
      icon: ModelTypeConstants.TEMPLATE_COLLECTION,
      handleOnClick: newEntryFromTemplate,
      value: 'create-entry-from-template',
    });

    if (isAgentTypeEnabled(AgentType.DATA_CAPTURE)) {
      entrySubmenuItems.push({
        name: <EntryFromComposeLabel />,
        pureTextName: 'Entry from Compose',
        icon: 'sparkle',
        handleOnClick: () => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLICKED_GLOBAL_ENTRY_FROM_COMPOSE, {
            actionOrigin: ActionOrigin.SIDEPANEL_GLOBAL_CREATE,
          });

          openDraftChatInSidepanel(AgentType.DATA_CAPTURE);
        },
        value: 'create-entry-from-compose',
      });
    }

    // If there's only one item, show it directly without a submenu
    if (entrySubmenuItems.length === 1) {
      return entrySubmenuItems[0];
    }

    // Otherwise, show a submenu
    return {
      name: 'Entry',
      icon: FolderItemIcon.Entry,
      submenu: [entrySubmenuItems],
      value: 'create-entry',
    };
  }

  private getConnectionSchemaMenuItems(): Array<CreateMenuItem> {
    if (!isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_CREATE})) {
      return [];
    } else if (!this.props.connectionSchemas) {
      const loadingConnectionSchemas: CreateMenuItem = {
        ...LOADING_MENU_ITEM,
        value: 'loading-connection-schemas',
        submenu: undefined,
      };
      return [loadingConnectionSchemas];
    } else if (this.props.connectionSchemas.length === 0) {
      return [
        {
          disabled: true,
          name: 'No connection schemas',
          value: 'no-connection-schemas',
        },
      ];
    }
    return this.props.connectionSchemas.map((connectionSchema) => {
      const handleOnClick = (): void => {
        runModal(CreateConnectionModal, {connectionSchema});
      };
      return {name: connectionSchema.name, handleOnClick, value: connectionSchema.id};
    });
  }

  /**
   * Create a list of menu items for schemas of a particular folder item type.
   * TODO: Unify with FolderCreateDropdown.getTagSchemaMenuOptions
   * @param customHandleOnClick By default, the handleOnClick function of the returned menu items will launch
   *                            CreateBioentityModal. In order to override that behavior, pass a function
   *                            that, when called with a tag schema, returns a function with the desired
   *                            handleOnClick behavior for a click on that schema's menu item.
   * @param folderItemType The folder item type to filter by, e.g. ModelTypeConstants.BASIC_FOLDER_ITEM
   * @param noSchemasMenuItem A menu item to display if there are no tag schemas that pass both the folder
   *                          item type filter and the `schemaFilterFn` predicate
   * @param schemaFilterFn An optional predicate that schemas must pass to be included in the list
   * @param tagSchemaSubtype The type of tag schema that is being loaded. This is necessary for registering
   *                         onclick callbacks to pass to `SearchableMenu`
   * @private
   */
  private getTagSchemaMenuItems({
    customHandleOnClick,
    folderItemType,
    noSchemasMenuItem,
    schemaFilterFn,
    tagSchemaSubtype,
  }: {
    customHandleOnClick?: (tagSchema: TagSchemaListingWithFieldsGraphQL) => (e: ElementMouseEvent) => void;
    folderItemType: OligoAwareFolderItemType;
    noSchemasMenuItem?: CreateMenuItem;
    schemaFilterFn?: (tagSchema: TagSchemaListingWithFieldsGraphQL) => boolean;
    tagSchemaSubtype: string;
  }): Array<CreateMenuItem> {
    if (!isFeatureEnabled({feature: RegistryFeature.REGISTRY_CREATE})) {
      return [];
    } else if (!this.props.tagSchemas || this.props.interfaceIdsByTagSchemaIds == null) {
      const loadingTagSchemas: CreateMenuItem = {
        ...LOADING_MENU_ITEM,
        value: `loading-${tagSchemaSubtype}-tag-schemas`,
        submenu: undefined,
      };
      return [loadingTagSchemas];
    }

    let filteredSchemas = filterTagSchemasGraphQL(this.props.tagSchemas, {
      oligoAwareFolderItemTypes: [folderItemType],
    });

    if (schemaFilterFn) {
      filteredSchemas = filteredSchemas.filter((schema) => schemaFilterFn(schema));
    }

    if (filteredSchemas.length === 0 && noSchemasMenuItem != null) {
      return [noSchemasMenuItem];
    }

    return filteredSchemas.map((tagSchema) => {
      const handleOnClick =
        customHandleOnClick != null
          ? customHandleOnClick(tagSchema)
          : (): void => {
              CreateBioentityModal.open(CreateBioentityModal, {
                createBioentityArgs: {
                  ...CreateBioentityUtil.getDefaultCreateBioentityArgs(),
                  tagSchema,
                  folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
                },
              });
            };
      return {
        name: tagSchema.name,
        icon: tagSchema.iconId as BenchlingIconName,
        handleOnClick,
        value: tagSchema.id,
      };
    });
  }

  private getRequestSchemaMenuItems(): Array<CreateMenuItem> {
    if (!this.props.requestSchemas) {
      const loadingRequestSchemas: CreateMenuItem = {
        ...LOADING_MENU_ITEM,
        value: 'loading-request-schemas',
        submenu: undefined,
      };
      return [loadingRequestSchemas];
    } else if (this.props.requestSchemas.length === 0) {
      return [
        {
          disabled: true,
          name: `No ${getLegacyRequestObjectName({lowerCase: true})} schemas`,
          value: `no-${getLegacyRequestObjectName({lowerCase: true})}-schemas`,
        },
      ];
    }
    return this.props.requestSchemas.map((requestSchema) => {
      const handleOnClick = (): void => {
        getStore().dispatch(
          openCreateRequestModal(
            getInitialRequestProps(
              requestSchema,
              this.props.requestTemplatesById,
              this.props.requestTaskSchemasById
            )
          )
        );
      };
      return {name: requestSchema.name, handleOnClick, value: requestSchema.sqlIdentifier};
    });
  }

  _makeOptionDatas(createMenuSections: Array<Array<CreateMenuItem>>): Array<OptionData> {
    const optionDatas: Array<OptionData> = [];
    for (const [sectionIndex, menuSection] of createMenuSections.entries()) {
      if (menuSection.every((item) => item.visible === false)) {
        continue;
      }
      const isInLastSection = sectionIndex === createMenuSections.length - 1;
      for (const [itemIndex, menuItem] of menuSection.entries()) {
        if (
          menuItem.visible === false ||
          (menuItem.submenu != null &&
            menuItem.submenu.flat().every((submenuItem) => submenuItem.visible === false))
        ) {
          continue;
        }
        const isEndOfSection = itemIndex === menuSection.length - 1;
        const optionDatum: OptionData = {
          className: classNames(menuItem.extraClasses ?? '', {
            [css(styles.sectionEnd)]: isEndOfSection && !isInLastSection,
          }),
          disabled: menuItem.disabled,
          hidden: !menuItem.visible,
          icon: menuItem.icon,
          iconClassName: menuItem.extraIconClasses,
          isHeader: menuItem.isHeader,
          name: menuItem.name,
          pureTextName: menuItem.pureTextName,
          value: menuItem.value,
          submenu: menuItem.submenu ? [this._makeOptionDatas(menuItem.submenu)] : null,
          dataTestAttrs: menuItem.dataTestAttrs,
        };
        optionDatas.push(optionDatum);
      }
    }
    return optionDatas;
  }

  override render(): JSX.Element {
    // Every menu option in the entire menu, all in a single array
    const flattenedMenuOptions = this._getMenuSections()
      .flat()
      .flatMap((menuOption) => [menuOption, ...(menuOption.submenu ?? []).flat()]);
    // A map from menu option value to an onclick callback for that option. This is necessary because
    // SearchableMenu assumes it is being used with `select` semantics; its `onSelect` callback provides
    // `optionData.value`. We use that `value` to decide the onclick behavior.
    const clickHandlerByOptionValue = flattenedMenuOptions.reduce<{
      [value: string]: () => void;
    }>((acc, option) => {
      // Some of these options exist only to launch submenus on hover, and should noop on onclick
      acc[option.value] = (option.handleOnClick as () => void) ?? noop;
      return acc;
    }, {});

    return (
      <Dropdown
        closeOnInsideClick={true}
        closeOnOutsideClick={true}
        isOpen={this.props.isOpen}
        onToggle={this.props.onToggle}
        menu={
          <SearchableMenu
            className="dropdown-menu--dropRight"
            height={MenuHeight.LARGE}
            width={MenuWidth.LARGE}
            optionDatas={this._makeOptionDatas(this._getMenuSections())}
            onSelect={(value) => {
              assert(
                clickHandlerByOptionValue.hasOwnProperty(value),
                `${value} has no associated click handler.`
              );
              clickHandlerByOptionValue[value]();
            }}
            // The scrollPaddingTop is used to offset the top of the menu to account for the height of the
            // search input; it has no visual impact on what's actually rendered. It matches minHeight for
            // '.menu--large .menu-option'. Practically, this only impacts Cypress, since we don't autoscroll
            // to an option using anything else.
            style={{scrollPaddingTop: 48}}
          />
        }
      >
        {this.props.children}
      </Dropdown>
    );
  }
}

export const ALL_CONNECTION_SCHEMAS_QUERY: DocumentNode = gql`
  query AllConnectionSchemasQuery @skipAudit @engComponent(name: NAVIGATION) {
    ...AllConnectionSchemasGraphQL
  }
  ${getFragmentWrapper('AllConnectionSchemas').fragment}
`;

export const requeryCreateMenuTagSchemas = (): void => {
  const tagSchemaQuery = readQuerySafe<AllTagSchemasQuery>({query: ALL_TAG_SCHEMAS_QUERY});

  if (!tagSchemaQuery) {
    return;
  }

  doQuerySafe<AllTagSchemasQuery>({query: ALL_TAG_SCHEMAS_QUERY}, {fetchPolicy: 'network-only'});
};

export default function CreateMenu({
  children,
  isOpen,
  onToggle,
}: {
  children: JSX.Element;
  isOpen: boolean;
  onToggle: (newIsOpen: boolean) => void;
}): JSX.Element {
  // TODO(GraphQL): Migrate ErrorHandlingQuery usage to useBenchlingQuery.
  const processTaskSchemas = useProcessTaskSchemasForCreate();
  const studySchemaCreateOptions = useStudySchemasAsLegacyDropdownMenuItems(
    null,
    ActionOrigin.SIDEPANEL_GLOBAL_CREATE
  );
  const complexPolymerSchemaCreateOptions = useComplexPolymerSchemasAsLegacyCreateDropdownOptions(null);
  const complexPolymersSchemasResult = useSkippableBenchlingQuery<
    CreateMenuComplexPolymersQuery,
    CreateMenuComplexPolymersQueryVariables
  >(CREATE_MENU_COMPLEX_POLYMERS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
  });
  const allComplexPolymerSchemaConfigs = getComplexPolymerSchemaConfigs(complexPolymersSchemasResult);

  const tagSchemasResult = useBenchlingQuery<AllTagSchemasQuery>(ALL_TAG_SCHEMAS_QUERY);
  const tagSchemasSSIResult = useBenchlingQuery<TagSchemasSSIQuery>(TAG_SCHEMAS_SSI_QUERY);
  const allSourcesResult = useBenchlingQuery<AllSourcesQuery>(ALL_SOURCES_QUERY);
  const assaySchemasResult = useBenchlingQuery<AllAssaySchemasQuery>(ALL_ASSAY_SCHEMAS_QUERY);
  const connectionSchemasResult = useBenchlingQuery<AllConnectionSchemasQuery>(ALL_CONNECTION_SCHEMAS_QUERY);

  const requestTaskSchemas = useAllRequestTaskSchemasQuery();
  const requestTemplates = useAllRequestTemplatesQuery();

  return (
    <>
      <PresentationalCreateMenu
        isOpen={isOpen}
        onToggle={onToggle}
        tagSchemas={
          tagSchemasResult.type === QueryResultType.SUCCESS
            ? filterTagSchemasGraphQL(tagSchemasResult.data.tagSchemas, {
                oligoAwareFolderItemTypes: [ModelTypeConstants.BASIC_FOLDER_ITEM, ModelTypeConstants.MIXTURE],
                requiredPermissions: ['objectsCreatable'],
              })
            : null
        }
        interfaceIdsByTagSchemaIds={
          tagSchemasSSIResult.type === QueryResultType.SUCCESS
            ? Object.fromEntries(
                tagSchemasSSIResult.data.tagSchemas.map((schema) => [
                  schema.id,
                  schema.interfaceConfigs.map((interfaceConfig) => interfaceConfig.interface.id),
                ])
              )
            : null
        }
        requestSchemas={
          assaySchemasResult.type === QueryResultType.SUCCESS
            ? graphQLArrayToREST('RequestSchema', assaySchemasResult.data.requestSchemas).filter(
                (schema) => !isArchived(schema)
              )
            : null
        }
        requestTemplatesById={requestTemplates != null ? _.indexBy(requestTemplates, 'id') : {}}
        requestTaskSchemasById={
          requestTaskSchemas != null
            ? objectFromEntries(requestTaskSchemas.map((schema) => [schema.id, schema]))
            : {}
        }
        registries={
          allSourcesResult.type === QueryResultType.SUCCESS
            ? graphQLArrayToREST(
                'NavStoreRegistry',
                allSourcesResult.data.sources.flatMap((source) =>
                  source.__typename === 'RegistryNode' ? [source] : []
                )
              )
            : []
        }
        connectionSchemas={
          connectionSchemasResult.type === QueryResultType.SUCCESS
            ? connectionSchemasResult.data.connectionSchemas.filter(
                (connectionSchema) => connectionSchema.permissions.objectsCreatable
              )
            : null
        }
        processTaskSchemas={processTaskSchemas}
        studySchemaCreateOptions={studySchemaCreateOptions}
        complexPolymerSchemaCreateOptions={complexPolymerSchemaCreateOptions}
        complexPolymerSchemaConfigurations={allComplexPolymerSchemaConfigs}
        actionOrigin={ACTION_ORIGIN}
      >
        {children}
      </PresentationalCreateMenu>
    </>
  );
}

const styles = StyleSheet.create({
  sectionEnd: {
    borderBottom: `1px solid ${Color.GRAY_200}`,
  },
});
